import type { ReadOnlyStore } from "./ReadOnlyStore";

export interface User {
  id: string;
  email: string;
  name: string;
  cognitoSub: string;
}

export interface Site {
  id: string;
  name: string;
  region?: string;
  permissions: string[];
}

export interface Cage {
  id: string;
  siteId: string;
  name: string;
  position?: number;
}

export interface Permission {
  name: string;
  active: boolean;
  sites: number[];
}

export type ToastLevel = "info" | "success" | "warn" | "error";

export type ShellMode = "shell" | "standalone";

export interface PageMeta {
  title: string;
  icon: string | null;
  withDates: boolean;
  hideSitePicker: boolean;
  isSingleSite: boolean;
}

export const DEFAULT_PAGE_META: PageMeta = {
  title: "",
  icon: null,
  withDates: false,
  hideSitePicker: false,
  isSingleSite: false,
};

export interface ShellHandle {
  auth: {
    getToken(): Promise<string>;
    onTokenRefresh(cb: (token: string) => void): () => void;
    currentUser: ReadOnlyStore<User | null>;
  };
  permissions: {
    has(perm: string): boolean;
    list(): string[];
    raw(): Permission[];
    onChange(cb: () => void): () => void;
  };
  sites: {
    list: ReadOnlyStore<Site[]>;
    selectedSiteId: ReadOnlyStore<string | null>;
    setSelectedSite(id: string): void;
    refresh(): Promise<void>;
    selectedSiteIds: ReadOnlyStore<number[]>;
    setSelectedSiteIds(ids: number[]): void;
    selectedCageIds: ReadOnlyStore<string[]>;
    setSelectedCageIds(ids: string[]): void;
  };
  dateRange: {
    range: ReadOnlyStore<{ start: string | null; end: string | null }>;
    setRange(start: string | null, end: string | null): void;
  };
  pageMeta: {
    meta: ReadOnlyStore<PageMeta>;
    set(meta: Partial<PageMeta>): void;
    clear(): void;
  };
  cages: {
    listForSite(siteId: string): ReadOnlyStore<Cage[]>;
    refresh(siteId: string): Promise<void>;
  };
  nav: {
    navigate(path: string, opts?: { replace?: boolean }): void;
    back(): void;
    currentPath: ReadOnlyStore<string>;
  };
  i18n: {
    language: ReadOnlyStore<string>;
    setLanguage(lang: string): void;
  };
  notify(msg: string, level: ToastLevel): void;
  mode: ShellMode;
  topbarSlots: {
    /** Shell-owned: DOM node MFEs portal their content into. */
    left: ReadOnlyStore<HTMLElement | null>;
    center: ReadOnlyStore<HTMLElement | null>;
    setLeft(el: HTMLElement | null): void;
    setCenter(el: HTMLElement | null): void;
    /** MFE-owned: tells shell to hide its own fallback rendering for that slot. */
    leftClaimed: ReadOnlyStore<boolean>;
    centerClaimed: ReadOnlyStore<boolean>;
    setLeftClaimed(v: boolean): void;
    setCenterClaimed(v: boolean): void;
  };
}
